// CalculadoraConductosView.js — Calculadora de Conductos: a partir de
// estancias + tramos, calcula la sección de cada tramo (método de
// velocidad constante), la pieza de plancha desplegada, y genera el plano
// de corte sobre planchas de 3x1,2m — para entregar a los técnicos antes
// de empezar a montar, minimizando desperdicio de fibra.
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { authService } from '../../services/authService';
import { calcularProyecto, DEFAULTS } from '../../utils/ductCalc';
import { contarCodos } from '../../utils/planoGeo';
import PlanoTracer from './PlanoTracer';

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white';
const labelCls = 'block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1';

function Field({ label, children }) {
  return <div><label className={labelCls}>{label}</label>{children}</div>;
}

function Spinner() {
  return <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />;
}

const fmtDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('T')[0].split('-');
  return `${d}/${m}/${y}`;
};

let uidCounter = 0;
const nextId = () => `id${Date.now()}_${uidCounter++}`;

const EMPTY_PROYECTO = { nombreProyecto: '', clienteNombre: '', potenciaKw: '', alturaDisponible: '20' };
const EMPTY_CONFIG = {
  velocidadTroncal: DEFAULTS.velocidadTroncal,
  caudalPorM2: DEFAULTS.caudalPorM2,
  merma: DEFAULTS.merma * 100, // se edita en % en la UI
  caudalPorKw: 170, // m³/h de aire por kW de potencia frigorífica del equipo — cifra de referencia del sector, ajustable
};

// ── Vista previa de una plancha (SVG a escala) ───────────────────────────────
function PlanchaPreview({ plancha, index }) {
  const VB_W = 300, VB_H = 120; // proporción 3000x1200mm reducida a escala 1/10
  return (
    <div className="border border-gray-200 rounded-xl p-3 bg-gray-50">
      <p className="text-xs font-bold text-gray-600 mb-2">Plancha {index + 1}</p>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full border border-gray-300 bg-white rounded">
        {plancha.piezas.map((p, i) => (
          <g key={i}>
            <rect
              x={p.x / 10} y={p.y / 10} width={p.largoPiezaMm / 10} height={p.anchoPiezaMm / 10}
              fill="#fee2e2" stroke="#b91c1c" strokeWidth="0.5"
            />
            <text x={(p.x + p.largoPiezaMm / 2) / 10} y={(p.y + p.anchoPiezaMm / 2) / 10}
              fontSize="4" textAnchor="middle" fill="#374151" dominantBaseline="middle">
              {p.etiqueta.length > 22 ? p.etiqueta.slice(0, 20) + '…' : p.etiqueta}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function CalculadoraConductosView() {
  const [proyecto, setProyecto] = useState(EMPTY_PROYECTO);
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [estancias, setEstancias] = useState([]);
  const [tramos, setTramos] = useState([]);
  const [savedId, setSavedId] = useState(null);
  const [numero, setNumero] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [msg, setMsg] = useState(null);
  const [planoFile, setPlanoFile] = useState(null);
  const [bgSnapshotUrl, setBgSnapshotUrl] = useState(null); // fondo de un proyecto reabierto (data URL ya guardado)
  const planoTracerRef = useRef(null);
  const [mmPorPx, setMmPorPx] = useState(null);
  const [equipoPos, setEquipoPos] = useState(null);
  const [mostrarProyectos, setMostrarProyectos] = useState(false);
  const [proyectosGuardados, setProyectosGuardados] = useState(null); // null = aún no se ha pedido la lista
  const [cargandoProyectos, setCargandoProyectos] = useState(false);
  const [abriendoProyecto, setAbriendoProyecto] = useState(false);
  const [buscarProyecto, setBuscarProyecto] = useState('');

  // ── Historial (deshacer/rehacer) ────────────────────────────────────────
  // Un snapshot es simplemente {estancias, tramos, equipoPos} — como todas
  // las mutaciones ya son inmutables (map/filter/spread, nunca se toca un
  // array/objeto existente), guardar la referencia actual antes de cada
  // cambio es válido y barato (no hace falta clonar). estadoActualRef se
  // mantiene sincronizado en cada render para que deshacer/rehacer (con
  // referencia estable, para poder colgarlas del atajo de teclado una sola
  // vez) siempre lean el estado más reciente, no uno capturado en un cierre
  // antiguo.
  const estadoActualRef = useRef({ estancias, tramos, equipoPos });
  estadoActualRef.current = { estancias, tramos, equipoPos };
  const historialRef = useRef({ pasado: [], futuro: [] });

  const aplicarCambio = useCallback((mutar) => {
    const h = historialRef.current;
    h.pasado.push(estadoActualRef.current);
    if (h.pasado.length > 60) h.pasado.shift();
    h.futuro = [];
    mutar();
  }, []);

  const restaurarSnapshot = (snap) => {
    setEstancias(snap.estancias);
    setTramos(snap.tramos);
    setEquipoPos(snap.equipoPos);
  };
  const deshacer = useCallback(() => {
    const h = historialRef.current;
    if (!h.pasado.length) return;
    const anterior = h.pasado.pop();
    h.futuro.push(estadoActualRef.current);
    restaurarSnapshot(anterior);
  }, []);
  const rehacer = useCallback(() => {
    const h = historialRef.current;
    if (!h.futuro.length) return;
    const siguiente = h.futuro.pop();
    h.pasado.push(estadoActualRef.current);
    restaurarSnapshot(siguiente);
  }, []);
  const puedeDeshacer = historialRef.current.pasado.length > 0;
  const puedeRehacer = historialRef.current.futuro.length > 0;

  // Ctrl+Z / Ctrl+Y (y Ctrl+Shift+Z) — se ignora si el foco está en un
  // campo de texto, para no robarle el deshacer nativo del navegador.
  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === 'z' && !e.shiftKey) { e.preventDefault(); deshacer(); }
      else if ((e.ctrlKey || e.metaKey) && (k === 'y' || (k === 'z' && e.shiftKey))) { e.preventDefault(); rehacer(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [deshacer, rehacer]);

  const setP = (k, v) => { setProyecto(p => ({ ...p, [k]: v })); setSavedId(null); setNumero(null); };
  const setC = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  // updateEstancia/updateTramo (edición de texto campo a campo) quedan
  // fuera del historial a propósito — si cada pulsación de tecla generara
  // un paso de deshacer, Ctrl+Z solo retrocedería un carácter cada vez. El
  // historial se centra en las acciones del propio plano (trazar, borrar,
  // mover, editar puntos), que es donde de verdad hace falta.
  const addEstancia = () => aplicarCambio(() => setEstancias(es => [...es, { id: nextId(), nombre: '', area: '', caudalManual: '' }]));
  const updateEstancia = (i, k, v) => setEstancias(es => es.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)));
  const removeEstancia = (i) => {
    const removed = estancias[i];
    aplicarCambio(() => {
      setEstancias(es => es.filter((_, idx) => idx !== i));
      if (removed) setTramos(ts => ts.map(t => ({ ...t, estanciasIds: t.estanciasIds.filter(id => id !== removed.id) })));
    });
  };

  const addTramo = () => aplicarCambio(() => setTramos(ts => [...ts, { id: nextId(), nombre: '', tipo: 'tronco', longitud: '', codos: 0, estanciasIds: [] }]));
  const updateTramo = (i, k, v) => setTramos(ts => ts.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const removeTramo = (i) => aplicarCambio(() => setTramos(ts => ts.filter((_, idx) => idx !== i)));

  // Variantes por id (en vez de índice) para el borrado desde el propio
  // canvas — PlanoTracer solo conoce el id de lo que hay seleccionado.
  const removeEstanciaPorId = (id) => aplicarCambio(() => {
    setEstancias(es => es.filter(e => e.id !== id));
    setTramos(ts => ts.map(t => ({ ...t, estanciasIds: t.estanciasIds.filter(eid => eid !== id) })));
  });
  const removeTramoPorId = (id) => aplicarCambio(() => setTramos(ts => ts.filter(t => t.id !== id)));
  const toggleTramoEstancia = (i, estanciaId) => aplicarCambio(() => setTramos(ts => ts.map((t, idx) => {
    if (idx !== i) return t;
    const tiene = t.estanciasIds.includes(estanciaId);
    return { ...t, estanciasIds: tiene ? t.estanciasIds.filter(id => id !== estanciaId) : [...t.estanciasIds, estanciaId] };
  })));

  // Merge en vez de reemplazar: al arrastrar el equipo solo cambian x/y, y
  // no debe perderse la rotación ni el tamaño ya elegidos. Colocar uno
  // nuevo (equipoPos era null) simplemente parte de {} y añade x/y —
  // rotacion/anchoMm/altoMm quedan undefined y se leen con su valor por
  // defecto al dibujar (ver EQUIPO_ANCHO_MM_DEFECTO en PlanoTracer.js).
  const handleColocarEquipo = (pos) => aplicarCambio(() => setEquipoPos(prev => (pos === null ? null : { ...prev, ...pos })));
  const handleRotarEquipo = () => aplicarCambio(() => setEquipoPos(prev => (prev ? { ...prev, rotacion: ((prev.rotacion || 0) + 90) % 360 } : prev)));
  const handleTamanoEquipo = (campo, valorMm) => setEquipoPos(prev => (prev ? { ...prev, [campo]: valorMm } : prev));

  // Edición de un punto ya trazado (arrastrar un vértice de un tramo o
  // estancia existente) — un paso de historial por edición terminada
  // (al soltar el arrastre), no por cada frame mientras se arrastra.
  const handleEditarPuntoTramo = (tramoId, indice, punto) => aplicarCambio(() => setTramos(ts => ts.map(t => {
    if (t.id !== tramoId || !t.trazadoPx) return t;
    const trazadoPx = t.trazadoPx.map((p, idx) => (idx === indice ? punto : p));
    return { ...t, trazadoPx };
  })));
  const handleEditarPuntoEstancia = (estanciaId, indice, punto) => aplicarCambio(() => setEstancias(es => es.map(e => {
    if (e.id !== estanciaId || !e.poligonoPx) return e;
    const poligonoPx = e.poligonoPx.map((p, idx) => (idx === indice ? punto : p));
    return { ...e, poligonoPx };
  })));

  // Accesorios en línea (compuerta/silenciador/filtro) — colgados del propio
  // tramo, no de una lista aparte, para que se borren solos si se borra el
  // tramo. No participan en ductCalc.js: son solo visuales/documentación,
  // el caudal/sección de la red sigue saliendo exclusivamente de las
  // estancias (ver la nota en el plan de esta fase — cambiar eso implicaría
  // rediseñar detectarConexiones/piezasUnion, que ya se dejaron verificados
  // contra los manuales de URSA AIR y no merece la pena arriesgar).
  const handleNuevoAccesorio = (tramoId, punto, tipo) => aplicarCambio(() => setTramos(ts => ts.map(t => (
    t.id === tramoId ? { ...t, accesorios: [...(t.accesorios || []), { id: nextId(), tipo, punto }] } : t
  ))));
  const handleEliminarAccesorio = (tramoId, accesorioId) => aplicarCambio(() => setTramos(ts => ts.map(t => (
    t.id === tramoId ? { ...t, accesorios: (t.accesorios || []).filter(a => a.id !== accesorioId) } : t
  ))));
  const handleTerminalTramo = (i, tipo) => aplicarCambio(() => setTramos(ts => ts.map((t, idx) => (
    idx === i ? { ...t, terminal: tipo ? { tipo } : null } : t
  ))));

  // El trazado sobre el plano solo captura geometría (área/longitud) — el
  // nombre, tipo, codos y a qué estancias alimenta un tramo se terminan de
  // rellenar en las tablas de abajo, que ya existían.
  const handlePlanoSeleccionado = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPlanoFile(file);
    setBgSnapshotUrl(null); // una subida nueva sustituye al snapshot de un proyecto reabierto
    setMmPorPx(null);
  };
  const handleNuevaEstanciaTrazada = ({ poligonoPx, areaM2 }) => aplicarCambio(() => {
    setEstancias(es => [...es, { id: nextId(), nombre: `Estancia ${es.length + 1}`, area: Math.round(areaM2 * 100) / 100, caudalManual: '', poligonoPx }]);
  });
  const handleNuevoTramoTrazado = ({ trazadoPx, longitudM }) => aplicarCambio(() => {
    // Los codos salen ya contados del propio trazado (el editor fuerza los
    // giros a 90°) — el campo sigue siendo editable por si se quiere forzar
    // otro valor.
    setTramos(ts => [...ts, { id: nextId(), nombre: `Tramo ${ts.length + 1}`, tipo: 'derivacion', longitud: Math.round(longitudM * 100) / 100, codos: contarCodos(trazadoPx), estanciasIds: [], trazadoPx }]);
  });

  const configNormalizada = useMemo(() => ({
    velocidadTroncal: Number(config.velocidadTroncal) || DEFAULTS.velocidadTroncal,
    caudalPorM2: Number(config.caudalPorM2) || DEFAULTS.caudalPorM2,
    merma: (Number(config.merma) || 0) / 100,
    alturaDisponibleMm: (Number(proyecto.alturaDisponible) || 0) * 10,
  }), [config, proyecto.alturaDisponible]);

  // El caudal del equipo ya no se teclea: sale de su potencia × la ratio
  // configurada (m³/h por kW) — mismo dato que antes, solo que derivado.
  // Se necesita antes de calcularProyecto: alimenta la ficha de fabricación
  // de la embocadura (estima la sección de salida del equipo a partir de él).
  const potenciaKwNum = Number(proyecto.potenciaKw) || 0;
  const caudalPorKwNum = Number(config.caudalPorKw) || EMPTY_CONFIG.caudalPorKw;
  const caudalEquipoNum = Math.round(potenciaKwNum * caudalPorKwNum);

  const resultado = useMemo(() => {
    if (!tramos.length || !estancias.length) return null;
    try {
      return calcularProyecto({ estancias, tramos, config: configNormalizada, equipoPos, caudalEquipoM3h: caudalEquipoNum });
    } catch {
      return null;
    }
  }, [estancias, tramos, configNormalizada, equipoPos, caudalEquipoNum]);

  const caudalEstancias = resultado?.resumen.caudalTotalEstanciasM3h || 0;
  const excedeCaudal = caudalEquipoNum > 0 && caudalEstancias > caudalEquipoNum;

  // El plano necesita los tramos con su sección/caudal ya calculados para
  // poder etiquetarlos ("200×150 · 310 m³/h") — se enriquecen aquí en vez
  // de duplicar el cálculo dentro de PlanoTracer.
  const tramosParaPlano = useMemo(() => {
    if (!resultado) return tramos;
    const porId = Object.fromEntries(resultado.tramosCalculados.map(t => [t.id, t]));
    return tramos.map(t => ({ ...t, calculado: porId[t.id] || null }));
  }, [tramos, resultado]);

  const handleGuardar = async () => {
    setSaving(true); setMsg(null);
    try {
      // El plano se guarda como un snapshot del propio fondo ya renderizado
      // en el canvas (JPEG comprimido, directamente en el documento de
      // Firestore) — no como el archivo original subido a Storage. Evita
      // depender de Storage (que en este proyecto no está bien configurado
      // — la subida devolvía 404) y de paso simplifica reabrir el proyecto:
      // no hace falta volver a descargar ni reprocesar un PDF, la imagen ya
      // viene lista para pintar.
      let planoInfo = null;
      if (planoFile || bgSnapshotUrl) {
        const snapshot = planoTracerRef.current?.getFondoSnapshot();
        if (snapshot) {
          if (snapshot.length > 900_000) {
            throw new Error('El plano ocupa demasiado una vez comprimido para guardarlo — prueba con un archivo más simple o de menor resolución.');
          }
          planoInfo = { snapshot, mmPorPx, equipoPos };
        }
      }

      const resp = await fetch('/.netlify/functions/saveProyectoConductos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          id: savedId,
          nombreProyecto: proyecto.nombreProyecto,
          clienteNombre: proyecto.clienteNombre,
          potenciaKw: potenciaKwNum,
          caudalEquipo: caudalEquipoNum,
          alturaDisponible: Number(proyecto.alturaDisponible) || 0,
          estancias, tramos, config: configNormalizada,
          plano: planoInfo,
          resumen: resultado ? { numPlanchas: resultado.resumen.numPlanchas, areaTotalM2: resultado.resumen.areaTotalM2 } : null,
        }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error);
      setSavedId(data.id);
      setNumero(data.numero);
      setMsg({ type: 'ok', text: `Proyecto guardado como ${data.numero}` });
    } catch (e) {
      setMsg({ type: 'err', text: e.message || 'Error al guardar el proyecto' });
    } finally {
      setSaving(false);
    }
  };

  // Lista bajo demanda (no en el montaje) — solo hace falta pedirla cuando
  // el usuario realmente quiere ver/abrir un proyecto guardado.
  const cargarProyectosGuardados = async () => {
    setCargandoProyectos(true);
    try {
      const resp = await fetch('/.netlify/functions/getProyectosConductos', { headers: { ...authService.authHeader() } });
      const data = await resp.json();
      setProyectosGuardados(data.success ? data.proyectos : []);
    } catch {
      setProyectosGuardados([]);
    } finally {
      setCargandoProyectos(false);
    }
  };
  const handleAbrirListaProyectos = () => {
    setMostrarProyectos(true);
    if (proyectosGuardados === null) cargarProyectosGuardados();
  };

  const proyectosFiltrados = useMemo(() => {
    if (!proyectosGuardados) return [];
    const q = buscarProyecto.trim().toLowerCase();
    if (!q) return proyectosGuardados;
    return proyectosGuardados.filter(p =>
      (p.nombreProyecto || '').toLowerCase().includes(q) ||
      (p.clienteNombre || '').toLowerCase().includes(q) ||
      (p.numero || '').toLowerCase().includes(q)
    );
  }, [proyectosGuardados, buscarProyecto]);

  const handleNuevoProyecto = () => {
    historialRef.current = { pasado: [], futuro: [] };
    setProyecto(EMPTY_PROYECTO);
    setConfig(EMPTY_CONFIG);
    setEstancias([]);
    setTramos([]);
    setSavedId(null);
    setNumero(null);
    setPlanoFile(null);
    setBgSnapshotUrl(null);
    setMmPorPx(null);
    setEquipoPos(null);
    setMsg(null);
  };

  // Reconstruye el formulario entero a partir de un documento de Firestore
  // (que ya trae estancias/tramos/config completos, no solo el resumen). El
  // plano no necesita ninguna descarga: el snapshot ya guardado es una data
  // URL lista para pasarle a PlanoTracer tal cual (ver bgSnapshotUrl ahí).
  const handleAbrirProyecto = async (p) => {
    setAbriendoProyecto(true); setMsg(null);
    historialRef.current = { pasado: [], futuro: [] };
    try {
      setProyecto({
        nombreProyecto: p.nombreProyecto || '',
        clienteNombre: p.clienteNombre || '',
        potenciaKw: p.potenciaKw ?? '',
        alturaDisponible: String(p.alturaDisponible ?? '20'),
      });
      const cfg = p.config || {};
      const potencia = Number(p.potenciaKw) || 0;
      // caudalPorKw no se guarda directamente (solo el caudal ya derivado) —
      // se reconstruye a partir de caudalEquipo/potenciaKw para que, al
      // reabrir, el desglose siga mostrando la misma ratio usada entonces.
      const caudalPorKwRestaurado = potencia > 0 && p.caudalEquipo ? Math.round(p.caudalEquipo / potencia) : EMPTY_CONFIG.caudalPorKw;
      setConfig({
        velocidadTroncal: cfg.velocidadTroncal ?? EMPTY_CONFIG.velocidadTroncal,
        caudalPorM2: cfg.caudalPorM2 ?? EMPTY_CONFIG.caudalPorM2,
        merma: cfg.merma != null ? cfg.merma * 100 : EMPTY_CONFIG.merma,
        caudalPorKw: caudalPorKwRestaurado,
      });
      setEstancias(p.estancias || []);
      setTramos(p.tramos || []);
      setEquipoPos(p.plano?.equipoPos || null);
      setMmPorPx(p.plano?.mmPorPx || null);
      setPlanoFile(null);
      setBgSnapshotUrl(p.plano?.snapshot || null);

      setSavedId(p.id);
      setNumero(p.numero);
      setMostrarProyectos(false);
      setMsg({ type: 'ok', text: `Proyecto ${p.numero} abierto` });
    } catch (e) {
      setMsg({ type: 'err', text: 'No se pudo abrir el proyecto: ' + e.message });
    } finally {
      setAbriendoProyecto(false);
    }
  };

  const handleGenerarPDF = async () => {
    if (!resultado) return;
    setGenerandoPDF(true); setMsg(null);
    try {
      const resp = await fetch('/.netlify/functions/getPlanoConductosPDF', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          proyecto: {
            numero, nombreProyecto: proyecto.nombreProyecto, clienteNombre: proyecto.clienteNombre,
            caudalEquipo: caudalEquipoNum, alturaDisponible: proyecto.alturaDisponible,
          },
          resultado,
        }),
      });
      if (!resp.ok) { const d = await resp.json().catch(() => ({})); throw new Error(d.error || 'No se pudo generar el PDF'); }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setMsg({ type: 'err', text: e.message });
    } finally {
      setGenerandoPDF(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg border p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Calculadora de conductos</h2>
            <p className="text-gray-500 text-sm mt-1">
              Diseña la red de conductos, calcula la plancha necesaria y genera el plano de corte para entregar al técnico.
            </p>
            {numero && <p className="text-xs text-gray-400 mt-1.5">Proyecto guardado como <span className="font-semibold text-gray-600">{numero}</span></p>}
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={deshacer} disabled={!puedeDeshacer} title="Deshacer (Ctrl+Z)"
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg">↶ Deshacer</button>
            <button onClick={rehacer} disabled={!puedeRehacer} title="Rehacer (Ctrl+Y)"
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg">↷ Rehacer</button>
            <button onClick={handleNuevoProyecto} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg">+ Nuevo proyecto</button>
            <button onClick={handleAbrirListaProyectos} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg">Mis proyectos</button>
          </div>
        </div>
      </div>

      {mostrarProyectos && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setMostrarProyectos(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-800">Mis proyectos</h3>
              <button onClick={() => setMostrarProyectos(false)} className="text-gray-300 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="p-4 border-b border-gray-100">
              <input className={inputCls} placeholder="Buscar por número, nombre o cliente…" value={buscarProyecto} onChange={e => setBuscarProyecto(e.target.value)} />
            </div>
            <div className="overflow-y-auto flex-1 p-2">
              {cargandoProyectos && <p className="text-sm text-gray-400 text-center py-8">Cargando…</p>}
              {!cargandoProyectos && proyectosFiltrados.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-8">
                  {proyectosGuardados?.length ? 'Sin resultados para esa búsqueda.' : 'Todavía no hay proyectos guardados.'}
                </p>
              )}
              {proyectosFiltrados.map(p => (
                <button key={p.id} onClick={() => handleAbrirProyecto(p)} disabled={abriendoProyecto}
                  className="w-full text-left px-3 py-3 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center justify-between gap-3 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">{p.nombreProyecto || 'Sin nombre'} <span className="text-gray-400 font-normal">· {p.numero}</span></p>
                    <p className="text-xs text-gray-400">{p.clienteNombre || 'Sin cliente'} · {fmtDate(p.updatedAt)}</p>
                  </div>
                  {p.resumen && <p className="text-xs text-gray-400 shrink-0 text-right">{p.resumen.numPlanchas} plancha{p.resumen.numPlanchas === 1 ? '' : 's'}<br />{p.resumen.areaTotalM2} m²</p>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {msg && (
        <div className={`rounded-xl px-4 py-3 text-sm flex items-center justify-between ${
          msg.type === 'ok' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {msg.text}
          <button onClick={() => setMsg(null)} className="ml-4 font-bold opacity-60 hover:opacity-100">×</button>
        </div>
      )}

      {/* Datos del proyecto + configuración */}
      <div className="bg-white rounded-2xl shadow-lg border p-6 space-y-4">
        <h3 className="font-bold text-gray-800">Datos del proyecto</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Nombre del proyecto"><input className={inputCls} value={proyecto.nombreProyecto} onChange={e => setP('nombreProyecto', e.target.value)} placeholder="Ej: Vivienda C/ Mayor 4" /></Field>
          <Field label="Cliente (opcional)"><input className={inputCls} value={proyecto.clienteNombre} onChange={e => setP('clienteNombre', e.target.value)} /></Field>
          <Field label="Potencia del equipo (kW)">
            <input type="number" step="0.1" className={inputCls} value={proyecto.potenciaKw} onChange={e => setP('potenciaKw', e.target.value)} />
            {potenciaKwNum > 0 && <p className="text-[10px] text-gray-400 mt-1">≈ {caudalEquipoNum} m³/h de caudal ({caudalPorKwNum} m³/h por kW)</p>}
          </Field>
          <Field label="Altura disponible (cm)"><input type="number" className={inputCls} value={proyecto.alturaDisponible} onChange={e => setP('alturaDisponible', e.target.value)} /></Field>
        </div>

        <h3 className="font-bold text-gray-800 pt-2">Configuración de cálculo</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Field label="Velocidad inicial (m/s)"><input type="number" step="0.1" className={inputCls} value={config.velocidadTroncal} onChange={e => setC('velocidadTroncal', e.target.value)} /></Field>
          <Field label="Caudal específico (m³/h·m²)"><input type="number" className={inputCls} value={config.caudalPorM2} onChange={e => setC('caudalPorM2', e.target.value)} /></Field>
          <Field label="Caudal por kW equipo (m³/h·kW)"><input type="number" className={inputCls} value={config.caudalPorKw} onChange={e => setC('caudalPorKw', e.target.value)} /></Field>
          <Field label="Merma (%)"><input type="number" className={inputCls} value={config.merma} onChange={e => setC('merma', e.target.value)} /></Field>
        </div>
        <p className="text-xs text-gray-400">
          La velocidad inicial solo fija el primer tramo de cada rama que sale del equipo. El resto de la red se dimensiona por recuperación estática (método URSA AIR): la sección de cada tramo siguiente sale de igualar su pérdida de carga con la recuperación producida por la caída de velocidad respecto al tramo anterior — así la velocidad baja de forma natural según se aleja del equipo.
        </p>
      </div>

      {/* Plano */}
      <div className="bg-white rounded-2xl shadow-lg border p-6 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800">Plano</h3>
            <p className="text-xs text-gray-400 mt-0.5">Opcional — sube el plano (PDF, JPG o PNG) para calibrar la escala y dibujar estancias/tramos directamente encima, en vez de escribirlos a mano.</p>
          </div>
          <label className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg cursor-pointer">
            {planoFile || bgSnapshotUrl ? 'Cambiar plano' : 'Subir plano'}
            <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={handlePlanoSeleccionado} />
          </label>
        </div>
        <PlanoTracer
          ref={planoTracerRef}
          file={planoFile}
          bgSnapshotUrl={bgSnapshotUrl}
          mmPorPx={mmPorPx}
          onCalibrar={setMmPorPx}
          estancias={estancias}
          tramos={tramosParaPlano}
          equipoPos={equipoPos}
          junctions={resultado?.juntas}
          onColocarEquipo={handleColocarEquipo}
          onRotarEquipo={handleRotarEquipo}
          onTamanoEquipo={handleTamanoEquipo}
          onNuevaEstancia={handleNuevaEstanciaTrazada}
          onNuevoTramo={handleNuevoTramoTrazado}
          onEliminarEstancia={removeEstanciaPorId}
          onEliminarTramo={removeTramoPorId}
          onEditarPuntoTramo={handleEditarPuntoTramo}
          onEditarPuntoEstancia={handleEditarPuntoEstancia}
          onNuevoAccesorio={handleNuevoAccesorio}
          onEliminarAccesorio={handleEliminarAccesorio}
        />
      </div>

      {/* Estancias */}
      <div className="bg-white rounded-2xl shadow-lg border p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Estancias</h3>
          <button onClick={addEstancia} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg">+ Añadir estancia</button>
        </div>
        {estancias.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">Sin estancias todavía.</p>}
        {estancias.map((e, i) => (
          <div key={e.id} className="grid grid-cols-12 gap-2 items-end bg-gray-50 rounded-lg p-3">
            <div className="col-span-4"><Field label="Nombre"><input className={inputCls} value={e.nombre} onChange={ev => updateEstancia(i, 'nombre', ev.target.value)} placeholder="Ej: Salón" /></Field></div>
            <div className="col-span-3"><Field label="Área (m²)"><input type="number" className={inputCls} value={e.area} onChange={ev => updateEstancia(i, 'area', ev.target.value)} /></Field></div>
            <div className="col-span-4"><Field label="Caudal manual (m³/h, opcional)"><input type="number" className={inputCls} value={e.caudalManual} onChange={ev => updateEstancia(i, 'caudalManual', ev.target.value)} /></Field></div>
            <div className="col-span-1"><button onClick={() => removeEstancia(i)} className="w-full py-2 text-gray-300 hover:text-red-500">✕</button></div>
          </div>
        ))}
      </div>

      {/* Tramos */}
      <div className="bg-white rounded-2xl shadow-lg border p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-800">Tramos de la red</h3>
          <button onClick={addTramo} disabled={!estancias.length} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white text-xs font-semibold rounded-lg">+ Añadir tramo</button>
        </div>
        {!estancias.length && <p className="text-sm text-gray-400 py-2">Añade primero al menos una estancia.</p>}
        {tramos.map((t, i) => (
          <div key={t.id} className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4"><Field label="Nombre"><input className={inputCls} value={t.nombre} onChange={ev => updateTramo(i, 'nombre', ev.target.value)} placeholder="Ej: Tronco principal" /></Field></div>
              <div className="col-span-3"><Field label="Tipo"><select className={inputCls} value={t.tipo} onChange={ev => updateTramo(i, 'tipo', ev.target.value)}><option value="tronco">Tronco</option><option value="derivacion">Derivación</option></select></Field></div>
              <div className="col-span-2"><Field label="Longitud (m)"><input type="number" step="0.1" className={inputCls} value={t.longitud} onChange={ev => updateTramo(i, 'longitud', ev.target.value)} /></Field></div>
              <div className="col-span-2"><Field label="Codos"><input type="number" min="0" className={inputCls} value={t.codos} onChange={ev => updateTramo(i, 'codos', ev.target.value)} /></Field></div>
              <div className="col-span-1"><button onClick={() => removeTramo(i)} className="w-full py-2 text-gray-300 hover:text-red-500">✕</button></div>
            </div>
            <div>
              <label className={labelCls}>Alimenta a</label>
              <div className="flex flex-wrap gap-1.5">
                {estancias.map(e => (
                  <button key={e.id} onClick={() => toggleTramoEstancia(i, e.id)}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                      t.estanciasIds.includes(e.id) ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-gray-200 text-gray-500'
                    }`}>
                    {e.nombre || 'Sin nombre'}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-4">
                <Field label="Salida en el extremo">
                  <select className={inputCls} value={t.terminal?.tipo || ''} onChange={ev => handleTerminalTramo(i, ev.target.value || null)}>
                    <option value="">Sin salida (continúa la red)</option>
                    <option value="rejilla">Rejilla</option>
                    <option value="difusor">Difusor</option>
                  </select>
                </Field>
              </div>
              {t.accesorios?.length > 0 && (
                <div className="col-span-8 pb-2">
                  <span className="text-[11px] text-gray-400">
                    Accesorios en este tramo: {t.accesorios.map(a => a.tipo).join(', ')} — colócalos o quítalos desde el plano.
                  </span>
                </div>
              )}
            </div>
            {(() => {
              const conectadoA = resultado?.tramosCalculados.find(tc => tc.id === t.id)?.conectadoA;
              if (!conectadoA) return null;
              const destino = conectadoA.tipo === 'equipo' ? 'Equipo' : conectadoA.nombre;
              return <p className="text-[11px] text-gray-400">↳ conectado a: {destino} (detectado por el trazado en el plano)</p>;
            })()}
          </div>
        ))}
      </div>

      {/* Resultado */}
      {resultado && (
        <div className="bg-white rounded-2xl shadow-lg border p-6 space-y-4">
          <h3 className="font-bold text-gray-800">Resultado</h3>

          {excedeCaudal && (
            <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-2.5">
              El caudal sumado de las estancias ({Math.round(caudalEstancias)} m³/h) supera el caudal del equipo ({caudalEquipoNum} m³/h).
            </div>
          )}
          {resultado.resumen.avisos.map((a, i) => (
            <div key={i} className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-2.5">{a}</div>
          ))}
          {resultado.resumen.areaAdicionalM2 > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl px-4 py-2.5">
              Además, hay {resultado.resumen.areaAdicionalM2} m² de fibra (con merma) en piezas que no caben en una plancha estándar (ver avisos arriba) — no están incluidos en "Planchas necesarias" ni en "Fibra con merma", hay que comprarlos y encajarlos aparte.
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-gray-800">{resultado.resumen.numPlanchas}</div>
              <div className="text-xs text-gray-500 mt-0.5">Planchas necesarias</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-gray-800">{resultado.resumen.areaTotalM2} m²</div>
              <div className="text-xs text-gray-500 mt-0.5">Fibra con merma</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-gray-800">{Math.round(caudalEstancias)} m³/h</div>
              <div className="text-xs text-gray-500 mt-0.5">Caudal total estancias</div>
            </div>
          </div>

          {(() => {
            const conteo = {};
            for (const t of tramos) {
              if (t.terminal?.tipo) conteo[t.terminal.tipo] = (conteo[t.terminal.tipo] || 0) + 1;
              for (const a of t.accesorios || []) conteo[a.tipo] = (conteo[a.tipo] || 0) + 1;
            }
            const etiquetas = {
              difusor: ['difusor', 'difusores'], rejilla: ['rejilla', 'rejillas'], compuerta: ['compuerta', 'compuertas'],
              silenciador: ['silenciador', 'silenciadores'], filtro: ['filtro', 'filtros'],
            };
            const partes = Object.entries(conteo)
              .filter(([, n]) => n > 0)
              .map(([tipo, n]) => `${n} ${etiquetas[tipo][n === 1 ? 0 : 1]}`);
            if (!partes.length) return null;
            return <p className="text-xs text-gray-500">{partes.join(' · ')}</p>;
          })()}

          {resultado.fichasFabricacion?.length > 0 && (
            <p className="text-xs text-gray-400">
              El informe PDF incluye {resultado.fichasFabricacion.length} ficha{resultado.fichasFabricacion.length === 1 ? '' : 's'} de fabricación (cómo marcar y cortar cada tramo recto, codo, derivación o embocadura — método URSA AIR).
            </p>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {['Tramo', 'Tipo', 'Caudal', 'Velocidad', 'Sección', 'Pieza'].map(h => (
                    <th key={h} className="py-2 px-2 text-left text-xs font-bold text-gray-400 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resultado.tramosCalculados.map(t => (
                  <tr key={t.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-2 px-2 text-gray-700">{t.nombre || t.id}</td>
                    <td className="py-2 px-2 text-gray-500">{t.tipo === 'tronco' ? 'Tronco' : 'Derivación'}</td>
                    <td className="py-2 px-2 text-gray-500">{Math.round(t.caudalM3h)} m³/h</td>
                    <td className="py-2 px-2 text-gray-500">{t.velocidadMs?.toFixed(1)} m/s</td>
                    <td className="py-2 px-2 text-gray-500">{t.seccion.anchoMm}×{t.seccion.altoMm} mm</td>
                    <td className="py-2 px-2 text-gray-500">
                      {t.pieza.unidades > 1 ? `2× ` : ''}{Math.round(t.pieza.anchoPiezaMm)}×{Math.round(t.pieza.largoPiezaMm)} mm
                      {t.pieza.metodo === 'dosPiezasL' && <span className="text-[11px] text-gray-400"> (2 piezas en L)</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Vista previa del corte</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {resultado.planchas.map((pl, i) => <PlanchaPreview key={i} plancha={pl} index={i} />)}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={handleGuardar} disabled={saving}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 text-sm font-semibold rounded-lg">
              {saving && <Spinner />} Guardar proyecto
            </button>
            <button onClick={handleGenerarPDF} disabled={generandoPDF}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg">
              {generandoPDF && <Spinner />} Generar informe PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
